import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Shield, Key, QrCode, ShieldCheck, ShieldOff, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MFASettings() {
  return (
    <Layout>
      <div data-testid="mfa-page">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Autenticación Multi-Factor</h1>
          <p className="text-slate-400 mt-1">Protege tu cuenta con verificación en dos pasos (TOTP)</p>
        </div>
        <MFASection />
      </div>
    </Layout>
  );
}


/**
 * The MFA configuration card. Exported separately so it can be embedded
 * inside the Profile page (per product spec: MFA now lives under /perfil)
 * while keeping the standalone /mfa route alive for bookmarks.
 */
export function MFASection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollData, setEnrollData] = useState(null);
  const [showDisable, setShowDisable] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mfa/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      toast.error("Error cargando estado MFA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const startEnroll = async () => {
    try {
      const res = await axios.post(`${API}/mfa/enroll`, {}, { withCredentials: true });
      setEnrollData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar enrolamiento");
    }
  };

  return (
    <div data-testid="mfa-section">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="mfa-status-card">
              <div className="flex items-center gap-4 mb-4">
                {status?.mfa_enabled ? (
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <ShieldCheck size={24} className="text-emerald-400" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Shield size={24} className="text-amber-400" />
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    {status?.mfa_enabled ? "MFA Activado" : "MFA Desactivado"}
                  </h2>
                  <p className="text-sm text-slate-400">
                    {status?.mfa_enabled
                      ? `Activado el ${new Date(status.mfa_enabled_at).toLocaleDateString("es-MX")} | ${status.backup_codes_remaining} codigos de respaldo`
                      : "Tu cuenta no tiene proteccion adicional"}
                  </p>
                </div>
              </div>

              {status?.mfa_enabled ? (
                <div className="flex gap-3">
                  <Button variant="outline" className="text-red-400 border-red-500/20 hover:bg-red-500/10"
                    onClick={() => setShowDisable(true)} data-testid="disable-mfa-btn">
                    <ShieldOff size={16} className="mr-2" /> Desactivar MFA
                  </Button>
                </div>
              ) : (
                <Button onClick={startEnroll} data-testid="enable-mfa-btn">
                  <Key size={16} className="mr-2" /> Activar MFA
                </Button>
              )}
            </div>

            {/* Info */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-white mb-3">Acerca de MFA</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <QrCode size={14} className="mt-0.5 text-teal-400 shrink-0" />
                  Usa una app de autenticacion (Google Authenticator, Authy, etc.) para generar codigos temporales
                </li>
                <li className="flex items-start gap-2">
                  <Shield size={14} className="mt-0.5 text-teal-400 shrink-0" />
                  Los codigos se renuevan cada 30 segundos y son unicos para tu cuenta
                </li>
                <li className="flex items-start gap-2">
                  <Key size={14} className="mt-0.5 text-teal-400 shrink-0" />
                  Guarda tus codigos de respaldo en un lugar seguro por si pierdes acceso al autenticador
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Enrollment Dialog */}
        {enrollData && (
          <EnrollDialog data={enrollData} onClose={() => { setEnrollData(null); fetchStatus(); }}
            onBackupCodes={setBackupCodes} />
        )}

        {/* Disable Dialog */}
        {showDisable && (
          <DisableDialog onClose={() => setShowDisable(false)} onDisabled={fetchStatus} />
        )}

        {/* Backup Codes Dialog */}
        {backupCodes && (
          <BackupCodesDialog codes={backupCodes} onClose={() => setBackupCodes(null)} />
        )}
    </div>
  );
}

function EnrollDialog({ data, onClose, onBackupCodes }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!code || code.length < 6) { toast.error("Ingresa el codigo de 6 digitos"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/mfa/confirm`, { code }, { withCredentials: true });
      toast.success("MFA activado exitosamente");
      if (res.data.backup_codes) {
        onBackupCodes(res.data.backup_codes);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Codigo invalido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle>Configurar MFA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-400">
            Escanea este codigo QR con tu app de autenticacion:
          </p>
          {data.qr_code && (
            <div className="flex justify-center bg-white rounded-xl p-4">
              <img src={data.qr_code} alt="QR Code" className="w-48 h-48" data-testid="mfa-qr-code" />
            </div>
          )}
          <div className="bg-slate-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Clave manual:</p>
            <code className="text-sm text-teal-400 break-all" data-testid="mfa-secret">{data.secret}</code>
          </div>
          <div>
            <Label>Codigo de verificacion</Label>
            <Input className="bg-slate-800 border-slate-700 text-center text-lg tracking-widest"
              maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              data-testid="mfa-code-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={submitting} data-testid="mfa-confirm-btn">
            {submitting ? "Verificando..." : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisableDialog({ onClose, onDisabled }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleDisable = async () => {
    if (!code) { toast.error("Ingresa tu codigo MFA"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/mfa/disable`, { code }, { withCredentials: true });
      toast.success("MFA desactivado");
      onClose();
      onDisabled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Codigo invalido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="disable-mfa-dialog">
        <DialogHeader>
          <DialogTitle>Desactivar MFA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-red-400">Ingresa tu codigo actual para confirmar la desactivacion:</p>
          <div>
            <Label>Codigo MFA</Label>
            <Input className="bg-slate-800 border-slate-700 text-center text-lg tracking-widest"
              maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              data-testid="disable-mfa-code" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" onClick={handleDisable} disabled={submitting} data-testid="disable-mfa-confirm">
            {submitting ? "Desactivando..." : "Desactivar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackupCodesDialog({ codes, onClose }) {
  const copyAll = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    toast.success("Codigos copiados al portapapeles");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="backup-codes-dialog">
        <DialogHeader>
          <DialogTitle>Codigos de Respaldo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <p className="text-sm text-amber-400">
              Guarda estos codigos en un lugar seguro. Cada codigo solo se puede usar una vez.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {codes.map((c, i) => (
              <div key={i} className="bg-slate-800 rounded-lg px-3 py-2 text-center">
                <code className="text-sm text-teal-400 font-mono" data-testid={`backup-code-${i}`}>{c}</code>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={copyAll} className="w-full" data-testid="copy-backup-codes">
            <Copy size={14} className="mr-2" /> Copiar Todos
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="backup-codes-close">He guardado mis codigos</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
